import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError, formatRelative, formatUsd } from '../lib/api';
import { useAuth, scopedCan } from '../lib/auth';
import { matches, runningFirst } from '../lib/listing';
import { RecorderCard } from '../components/RecorderCard';
import { Button, Card, Alert, Spinner, Empty, Modal, Field, Input, Select, StatTile, Confirm } from '../components/ui';
import { SelectBox, SelectableRow } from '../components/ResourceGroups';

/**
 * MediaConnect flows on their own: a source and its outputs, passed through
 * as they arrive (server: services/flows.ts). The list lives on the Encoders
 * tab; this file is that list, the launch form and a flow's own page.
 */

// ===========================================================================
// Types and small helpers
// ===========================================================================

export interface FlowSource {
  protocol: string; port: number; host?: string; cidr?: string;
  minLatency?: number; maxLatency?: number; streamId?: string;
}

export interface FlowOutput {
  name: string; protocol: string; port: number; host?: string; cidr?: string;
  minLatency?: number; maxLatency?: number; smoothingLatency?: number;
  streamId?: string; remoteId?: string; enabled?: boolean;
}

interface FlowSpec { name: string; source: FlowSource; outputs: FlowOutput[] }

interface Options {
  sourceProtocols: string[]; outputProtocols: string[];
  defaultSource: FlowSource; defaultOutput: FlowOutput; placeholderHost: string;
  estimate: { usdPerHour: number; note: string };
  templates: { id: string; name: string; description: string; spec: FlowSpec; createdBy: string | null }[];
}

interface Costs { perHourNow: number; sessionUsd: number; estimatedToDate: number; hours: number }

export interface FlowSummary {
  id: string; name: string; state: string; progress: string | null; error: string | null; busy: boolean;
  stopAt: string | null; encoderId: string | null; createdAt: string; createdBy: string | null;
  source: FlowSource; outputs: number; usdPerHour: number; costs: Costs;
}

interface FlowDetailData {
  id: string; name: string; region: string; az: string | null; arn: string | null;
  state: string; status: string | null; progress: string | null; error: string | null; busy: boolean;
  stopAt: string | null; encoderId: string | null; createdAt: string;
  /** The signed-in user's level on this flow: view | use | edit | full. */
  access?: string | null;
  spec: FlowSpec; ingestIp: string | null; egressIp: string | null; sourceAddress: string | null;
  outputs: (FlowOutput & { arn: string | null; address: string | null })[];
  costs: Costs & { usdPerHour: number; billed: { total: number; byDay: { day: string; usd: number }[] } };
}

interface Live {
  status: string | null; thumbnail: string | null; at: string;
  metadata: {
    at: string | null; messages: { code: string; message: string }[];
    programs: { number: number | null; name: string; streams: {
      type: string; codec: string; pid: number | null; width: number | null; height: number | null;
      fps: string | null; channels: number | null; sampleRate: number | null;
    }[] }[];
  } | null;
  metrics: { source: Record<string, number>; outputs: Record<string, Record<string, number>>; at: string | null; error?: string } | null;
}

const PROTOCOL_LABEL: Record<string, string> = {
  'srt-listener': 'SRT listener', 'srt-caller': 'SRT caller', rist: 'RIST', rtp: 'RTP', 'rtp-fec': 'RTP-FEC',
  'zixi-push': 'Zixi push', 'zixi-pull': 'Zixi pull',
};
const TRANSITIONAL = ['creating', 'starting', 'stopping', 'deleting'];
const PULL = ['srt-listener', 'zixi-pull'];
const isPull = (p: string) => PULL.includes(p);
const isSrt = (p: string) => p.startsWith('srt');
const isZixi = (p: string) => p.startsWith('zixi');
const TIMER_CHOICES = [1, 2, 3, 4, 6, 8, 12, 24];

function StateBadge({ state, progress }: { state: string; progress?: string | null }) {
  const s = state.toLowerCase();
  const colour =
    s === 'running' ? 'var(--color-good)'
    : s === 'failed' ? 'var(--color-critical)'
    : TRANSITIONAL.includes(s) ? 'var(--color-warning)'
    : 'var(--color-ink-muted)';
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span className={`h-2 w-2 shrink-0 rounded-full ${TRANSITIONAL.includes(s) ? 'animate-pulse-soft' : ''}`} style={{ background: colour }} />
      <span className="text-ink-secondary">{s}{progress ? ` — ${progress}` : ''}</span>
    </span>
  );
}

function Copyable({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <span className="inline-flex items-center gap-2">
      <code className="break-all font-mono text-xs">{text}</code>
      <button className="text-xs text-series-1 hover:underline" onClick={() => {
        void navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
      }}>{copied ? 'copied' : 'copy'}</button>
    </span>
  );
}

/** "stops in 3h 12m", counting down. */
function Countdown({ to }: { to: string }) {
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 30_000); return () => clearInterval(t); }, []);
  const ms = Date.parse(to) - Date.now();
  if (ms <= 0) return <span>stopping now</span>;
  const h = Math.floor(ms / 3_600_000), m = Math.floor((ms % 3_600_000) / 60_000);
  return <span>stops in {h ? `${h}h ` : ''}{m}m</span>;
}

const describeSource = (s: FlowSource) =>
  `${PROTOCOL_LABEL[s.protocol] ?? s.protocol} ${s.protocol === 'srt-caller' ? `→ ${s.host}:${s.port}` : `:${s.port}`}` +
  (s.minLatency != null ? ` · ${s.minLatency} ms` : s.maxLatency != null ? ` · up to ${s.maxLatency} ms` : '');

/** The next name like OUT-2 that nothing on the flow has. */
function nextName(name: string, taken: string[]): string {
  const base = name.replace(/-\d+$/, '') || 'OUT';
  let n = 2;
  while (taken.includes(`${base}-${n}`)) n++;
  return `${base}-${n}`.slice(0, 40);
}

/** The next port no listener on the flow (source included) is using. */
function nextPort(from: number, source: FlowSource, outputs: FlowOutput[]): number {
  const used = new Set(outputs.filter((o) => o.protocol === 'srt-listener').map((o) => o.port));
  if (source.protocol !== 'srt-caller') used.add(source.port);
  let p = from;
  while (used.has(p) && p < 65535) p++;
  return p;
}

/**
 * A copy of an output to sit beside it. A listener takes the next free port,
 * so it is ready to use as it is; anything that pushes somewhere keeps the
 * same destination for the operator to change.
 */
export function duplicateOf(o: FlowOutput, source: FlowSource, outputs: FlowOutput[]): FlowOutput {
  const copy: FlowOutput = { ...o, name: nextName(o.name, outputs.map((x) => x.name)), enabled: undefined };
  if (o.protocol === 'srt-listener') copy.port = nextPort(o.port + 1, source, outputs);
  return copy;
}

// ===========================================================================
// Forms
// ===========================================================================

function SourceFields({ opts, value, onChange }: { opts: Options; value: FlowSource; onChange: (v: FlowSource) => void }) {
  const set = (patch: Partial<FlowSource>) => onChange({ ...value, ...patch });
  const p = value.protocol;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      <Field label="Source">
        <Select value={p} onChange={(e) => {
          const np = e.target.value;
          set({
            protocol: np,
            minLatency: isSrt(np) ? value.minLatency ?? 50 : undefined,
            maxLatency: np === 'rist' ? 2000 : isZixi(np) ? 6000 : undefined,
            port: isZixi(np) ? 2088 : value.port || 5000,
          });
        }}>
          {opts.sourceProtocols.map((x) => <option key={x} value={x}>{PROTOCOL_LABEL[x] ?? x}</option>)}
        </Select>
      </Field>
      {p === 'srt-caller' && (
        <Field label="Their host" error={value.host === opts.placeholderHost ? 'Put in their address' : null}>
          <Input value={value.host ?? ''} onChange={(e) => set({ host: e.target.value.trim() })} placeholder="sender.example.com" />
        </Field>
      )}
      <Field label={p === 'srt-caller' ? 'Their port' : 'Port'} hint={isZixi(p) ? 'Zixi always uses 2088' : undefined}>
        <Input type="number" min={1024} max={65535} disabled={isZixi(p)} value={value.port} onChange={(e) => set({ port: Number(e.target.value) })} />
      </Field>
      {p !== 'srt-caller' && (
        <Field label="Allowed from" hint="0.0.0.0/0 is anyone">
          <Input value={value.cidr ?? '0.0.0.0/0'} onChange={(e) => set({ cidr: e.target.value.trim() })} placeholder="0.0.0.0/0" />
        </Field>
      )}
      {isSrt(p) && (
        <Field label="Latency ms">
          <Input type="number" min={10} max={15000} value={value.minLatency ?? 50} onChange={(e) => set({ minLatency: Number(e.target.value) })} />
        </Field>
      )}
      {(p === 'rist' || isZixi(p)) && (
        <Field label="Max latency ms">
          <Input type="number" min={10} max={15000} value={value.maxLatency ?? 2000} onChange={(e) => set({ maxLatency: Number(e.target.value) })} />
        </Field>
      )}
      {(p === 'srt-caller' || isZixi(p)) && (
        <Field label="Stream ID">
          <Input value={value.streamId ?? ''} onChange={(e) => set({ streamId: e.target.value })} placeholder="optional" />
        </Field>
      )}
    </div>
  );
}

function OutputFields({ opts, value, onChange, nameLocked }: {
  opts: Options; value: FlowOutput; onChange: (v: FlowOutput) => void; nameLocked?: boolean;
}) {
  const set = (patch: Partial<FlowOutput>) => onChange({ ...value, ...patch });
  const p = value.protocol;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      <Field label="Name">
        {nameLocked
          ? <div className="py-2 text-sm text-ink-secondary">{value.name}</div>
          : <Input value={value.name} maxLength={40} onChange={(e) => set({ name: e.target.value.replace(/[^A-Za-z0-9_-]/g, '') })} placeholder="TVNZ" />}
      </Field>
      <Field label="Type">
        <Select value={p} onChange={(e) => {
          const np = e.target.value;
          set({
            protocol: np,
            minLatency: isSrt(np) ? value.minLatency ?? 50 : undefined,
            maxLatency: isZixi(np) ? 6000 : undefined,
            port: isZixi(np) ? 2088 : value.port || 7000,
          });
        }}>
          {opts.outputProtocols.map((x) => <option key={x} value={x}>{PROTOCOL_LABEL[x] ?? x}{isPull(x) ? ' - they pull' : ' - we push'}</option>)}
        </Select>
      </Field>
      {!isPull(p) && (
        <Field label="Their host" error={value.host === opts.placeholderHost ? 'Put in their address' : null}>
          <Input value={value.host ?? ''} onChange={(e) => set({ host: e.target.value.trim() })} placeholder="receiver.example.com" />
        </Field>
      )}
      <Field label={isPull(p) ? 'Port' : 'Their port'}>
        <Input type="number" min={1024} max={65535} disabled={isZixi(p)} value={value.port} onChange={(e) => set({ port: Number(e.target.value) })} />
      </Field>
      {isPull(p) && (
        <Field label="Allowed from" hint="0.0.0.0/0 is anyone">
          <Input value={value.cidr ?? '0.0.0.0/0'} onChange={(e) => set({ cidr: e.target.value.trim() })} />
        </Field>
      )}
      {isSrt(p) && (
        <Field label="Latency ms">
          <Input type="number" min={10} max={15000} value={value.minLatency ?? 50} onChange={(e) => set({ minLatency: Number(e.target.value) })} />
        </Field>
      )}
      {isZixi(p) && (
        <Field label="Max latency ms">
          <Input type="number" min={10} max={15000} value={value.maxLatency ?? 6000} onChange={(e) => set({ maxLatency: Number(e.target.value) })} />
        </Field>
      )}
      {(p === 'rtp' || p === 'rtp-fec' || p === 'rist') && (
        <Field label="Smoothing ms" hint="optional">
          <Input type="number" min={0} max={1000} value={value.smoothingLatency ?? ''} onChange={(e) => set({ smoothingLatency: e.target.value === '' ? undefined : Number(e.target.value) })} />
        </Field>
      )}
      {(p === 'srt-caller' || isZixi(p)) && (
        <Field label="Stream ID">
          <Input value={value.streamId ?? ''} onChange={(e) => set({ streamId: e.target.value })} placeholder="optional" />
        </Field>
      )}
      {p === 'zixi-pull' && (
        <Field label="Remote ID">
          <Input value={value.remoteId ?? ''} onChange={(e) => set({ remoteId: e.target.value })} />
        </Field>
      )}
    </div>
  );
}

function TimerSelect({ value, onChange }: { value: number | null; onChange: (h: number | null) => void }) {
  return (
    <Select value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}>
      <option value="">Never - until someone stops it</option>
      {TIMER_CHOICES.map((h) => <option key={h} value={h}>Stop after {h} hour{h === 1 ? '' : 's'}</option>)}
    </Select>
  );
}

const useOptions = () => {
  const [opts, setOpts] = useState<Options | null>(null);
  useEffect(() => { void api.get<Options>('/flows/options').then(setOpts).catch(() => setOpts(null)); }, []);
  return opts;
};

// ===========================================================================
// The list - a section of the Encoders tab
// ===========================================================================

export function FlowsSection({ query = '', onList, hide, selected, onToggle }: {
  query?: string;
  /** Told the list each time it loads (the Encoders page's groups use it). */
  onList?: (flows: FlowSummary[]) => void;
  /** `flow:<id>` keys left out of the list: they are shown in a group. */
  hide?: Set<string>;
  /** Given, each row has a tick box for grouping. */
  selected?: Set<string>;
  onToggle?: (key: string) => void;
}) {
  const { can } = useAuth();
  const [list, setList] = useState<FlowSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() =>
    api.get<{ flows: FlowSummary[] }>('/flows').then((r) => { setList(r.flows); onList?.(r.flows); setError(null); })
      .catch((err: Error) => setError(err.message)), [onList]);
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, [load]);

  const running = (list ?? []).filter((f) => f.state === 'running');
  const shown = runningFirst((list ?? []).filter((f) => !hide?.has(`flow:${f.id}`) && matches(query, f.name, describeSource(f.source), f.state, f.encoderId ? 'encoder' : '')));
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Flows</h2>
          <p className="text-sm text-ink-muted">
            MediaConnect on its own: a source passed straight through to its outputs.
            {running.length > 0 && ` ${running.length} running, ${formatUsd(running.reduce((s, f) => s + f.usdPerHour, 0), 2)}/hr.`}
          </p>
        </div>
        {can('resource:create') && <Link to="/launch/flow"><Button variant="primary">New flow</Button></Link>}
      </div>
      {error && <Alert tone="critical">{error}</Alert>}
      <Card>
        {!list ? <div className="p-4"><Spinner label="Loading flows…" /></div>
          : list.length === 0 ? <Empty title="No flows">Make one to take a feed in and hand it to any number of receivers.</Empty>
          : shown.length === 0 ? <div className="px-4 py-3 text-sm text-ink-muted">{query ? `No ungrouped flow matches "${query}".` : 'Every flow is in a group above.'}</div>
          : (
            <div className="divide-y divide-grid">
              {query && shown.length < list.length && (
                <div className="px-4 py-1.5 text-xs text-ink-muted">{shown.length} of {list.length}</div>
              )}
              {shown.map((f) => (
                <SelectableRow
                  key={f.id}
                  to={`/flows/${f.id}`}
                  box={selected && onToggle && <SelectBox checked={selected.has(`flow:${f.id}`)} onToggle={() => onToggle(`flow:${f.id}`)} label={f.name} />}
                >
                  <div className="basis-full sm:basis-auto min-w-0 flex-1">
                    <div className="font-medium">{f.name}</div>
                    <div className="text-xs text-ink-muted">
                      {describeSource(f.source)} · {f.outputs} output{f.outputs === 1 ? '' : 's'}
                      {f.encoderId && ' · has an encoder'}
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    <div className="tabular">{f.state === 'running' ? `${formatUsd(f.usdPerHour, 3)}/hr` : formatUsd(f.costs.estimatedToDate, 2)}</div>
                    <div className="text-ink-muted">
                      {f.state === 'running' ? (f.stopAt ? <Countdown to={f.stopAt} /> : `${formatUsd(f.costs.sessionUsd, 2)} this run`) : 'so far'}
                    </div>
                  </div>
                  <StateBadge state={f.state} progress={TRANSITIONAL.includes(f.state) ? f.progress : null} />
                  <span className="hidden w-24 text-right text-xs text-ink-muted sm:block">{formatRelative(f.createdAt)}</span>
                </SelectableRow>
              ))}
            </div>
          )}
      </Card>
    </div>
  );
}

// ===========================================================================
// Launch
// ===========================================================================

export function LaunchFlow() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const opts = useOptions();
  const [name, setName] = useState('');
  const [source, setSource] = useState<FlowSource | null>(null);
  const [outputs, setOutputs] = useState<FlowOutput[]>([]);
  const [start, setStart] = useState(true);
  const [hours, setHours] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [templateId, setTemplateId] = useState('');

  // The defaults, a template, or a copy of another flow (?from=<id>).
  useEffect(() => {
    if (!opts || source) return;
    const from = params.get('from');
    if (from) {
      void api.get<{ flow: FlowDetailData }>(`/flows/${from}`).then((r) => {
        setSource(r.flow.spec.source);
        setOutputs(r.flow.spec.outputs.map(({ enabled: _e, ...o }) => o));
        setName('');
      }).catch(() => { setSource(opts.defaultSource); setOutputs([opts.defaultOutput]); });
    } else {
      setSource(opts.defaultSource);
      setOutputs([opts.defaultOutput]);
    }
  }, [opts, source, params]);

  const applyTemplate = (tid: string) => {
    setTemplateId(tid);
    const t = opts?.templates.find((x) => x.id === tid);
    if (!t) return;
    setSource(t.spec.source);
    setOutputs(t.spec.outputs);
  };

  if (!opts || !source) return <Spinner label="Loading…" />;

  const placeholders = [source.host, ...outputs.map((o) => o.host)].filter((h) => h === opts.placeholderHost).length;
  const submit = async () => {
    setSaving(true); setError(null);
    try {
      const r = await api.post<{ id: string }>('/flows', { spec: { name, source, outputs }, start, stopAfterHours: start ? hours : null });
      navigate(`/flows/${r.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not launch the flow.');
    } finally { setSaving(false); }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div>
        <h1 className="text-lg font-semibold">New flow</h1>
        <p className="text-sm text-ink-muted">A MediaConnect flow: one source, passed through to each output as it arrives. Everything here can be changed once it is running.</p>
      </div>
      {error && <Alert tone="critical">{error}</Alert>}

      <Card title="Flow">
        <div className="grid gap-3 p-4 sm:grid-cols-2">
          <Field label="Name" hint="Letters, numbers, dashes. It is the flow's name in AWS.">
            <Input autoFocus value={name} maxLength={60} onChange={(e) => setName(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))} placeholder="NZR-FEED-1" />
          </Field>
          {opts.templates.length > 0 && (
            <Field label="From a template" hint={templateId ? (
              <button type="button" className="text-ink-muted hover:text-critical" onClick={() => {
                const t = opts.templates.find((x) => x.id === templateId);
                if (!t || !window.confirm(`Delete the template "${t.name}"? Flows made from it are not affected.`)) return;
                void api.delete(`/flows/templates/${t.id}`).then(() => window.location.reload()).catch((err: Error) => setError(err.message));
              }}>delete this template</button>
            ) : undefined}>
              <Select value={templateId} onChange={(e) => applyTemplate(e.target.value)}>
                <option value="">None</option>
                {opts.templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </Field>
          )}
        </div>
      </Card>

      <Card title="Source">
        <div className="p-4"><SourceFields opts={opts} value={source} onChange={setSource} /></div>
      </Card>

      <Card
        title={`Outputs (${outputs.length})`}
        action={<Button variant="ghost" onClick={() => setOutputs([...outputs, {
          ...opts.defaultOutput, name: outputs.length ? nextName(outputs[outputs.length - 1].name, outputs.map((o) => o.name)) : 'OUT-1',
          port: nextPort(7000, source, outputs),
        }])}>+ Add output</Button>}
      >
        <div className="space-y-2 p-4">
          {outputs.length === 0 && <div className="text-sm text-ink-muted">No outputs yet - they can be added at any time, while it runs.</div>}
          {outputs.map((o, i) => (
            <div key={i} className="space-y-1 rounded bg-plane/60 p-2">
              <OutputFields opts={opts} value={o} onChange={(v) => setOutputs(outputs.map((x, j) => (j === i ? v : x)))} />
              <div className="flex gap-3 text-xs">
                <button type="button" className="text-series-1 hover:underline"
                  onClick={() => setOutputs([...outputs.slice(0, i + 1), duplicateOf(o, source, outputs), ...outputs.slice(i + 1)])}>duplicate</button>
                <button type="button" className="text-ink-muted hover:text-critical" onClick={() => setOutputs(outputs.filter((_, j) => j !== i))}>remove</button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Running">
        <div className="grid gap-3 p-4 sm:grid-cols-2">
          <Field label="When it is made">
            <Select value={start ? 'start' : 'idle'} onChange={(e) => setStart(e.target.value === 'start')}>
              <option value="start">Start it straight away</option>
              <option value="idle">Leave it stopped - costs nothing until started</option>
            </Select>
          </Field>
          {start && <Field label="Stop timer"><TimerSelect value={hours} onChange={setHours} /></Field>}
        </div>
        <div className="border-t border-grid px-4 py-3 text-sm">
          <span className="tabular font-medium">{formatUsd(opts.estimate.usdPerHour, 3)}/hr</span>
          <span className="text-ink-muted"> while running{hours ? ` - about ${formatUsd(opts.estimate.usdPerHour * hours, 2)} for ${hours} h` : ''}. {opts.estimate.note}</span>
        </div>
      </Card>

      {placeholders > 0 && <Alert tone="warning">The template left {placeholders} address{placeholders === 1 ? '' : 'es'} blank ({opts.placeholderHost}) - put in where each one sends to.</Alert>}
      <div className="flex justify-end gap-2">
        <Link to="/encoders"><Button variant="ghost">Cancel</Button></Link>
        <Button variant="primary" loading={saving} disabled={name.length < 2 || placeholders > 0} onClick={() => void submit()}>
          {start ? 'Make and start' : 'Make flow'}
        </Button>
      </div>
    </div>
  );
}

// ===========================================================================
// One flow
// ===========================================================================

const METRIC_LABEL: Record<string, string> = {
  SourceBitRate: 'Bitrate', BitRate: 'Bitrate', OutputBitRate: 'Bitrate',
  SourceConnected: 'Connected', Connected: 'Connected', OutputConnected: 'Connected', ConnectedOutputs: 'Outputs connected',
  SourceDisconnections: 'Disconnections', Disconnections: 'Disconnections', OutputDisconnections: 'Disconnections',
  SourceRoundTripTime: 'Round trip', RoundTripTime: 'Round trip',
  SourcePacketLossPercent: 'Packet loss', PacketLossPercent: 'Packet loss',
  SourceDroppedPackets: 'Dropped packets', DroppedPackets: 'Dropped packets', OutputDroppedPayloads: 'Dropped payloads',
  SourceNotRecoveredPackets: 'Not recovered', NotRecoveredPackets: 'Not recovered',
  SourceRecoveredPackets: 'Recovered', RecoveredPackets: 'Recovered',
  SourceTotalPackets: 'Packets', TotalPackets: 'Packets',
  SourceARQRequests: 'Resend requests', ARQRequests: 'Resend requests', SourceARQRecovered: 'Resent and recovered', ARQRecovered: 'Resent and recovered',
  SourceContinuityCounter: 'Continuity errors', ContinuityCounter: 'Continuity errors',
  SourcePCRError: 'PCR errors', PCRError: 'PCR errors', SourcePCRAccuracyError: 'PCR accuracy errors',
  SourcePATError: 'PAT errors', PATError: 'PAT errors', SourcePMTError: 'PMT errors', PMTError: 'PMT errors',
  SourceTSSyncLoss: 'Sync loss', TSSyncLoss: 'Sync loss', SourceTSByteError: 'Byte errors', SourcePIDError: 'PID errors',
  SourceCATError: 'CAT errors', SourcePTSError: 'PTS errors', SourceCRCError: 'CRC errors', SourceTransportError: 'Transport errors',
  SourceOverflowPackets: 'Overflowed packets', SourceFECPackets: 'FEC packets', SourceFECRecovered: 'FEC recovered',
  SourceSelected: 'Selected', SourceMergeActive: 'Merge active', MaintenanceScheduled: 'Maintenance scheduled',
  FailoverSwitches: 'Failover switches', FlowOperationalStatus: 'Healthy',
  SourceLatency: 'Latency', OutputLatency: 'Latency', SourceJitter: 'Jitter', SourceUpTime: 'Up time',
  SourceRejections: 'Rejected connections', OutputRejections: 'Rejected connections', OutputRejectCode: 'Last reject code',
  OutputEnabled: 'Enabled', OutputTotalPackets: 'Packets', OutputResentPackets: 'Resent packets',
  OutputARQRequests: 'Resend requests', OutputRoundTripTime: 'Round trip', TimecodePresent: 'Timecode present',
  SourceMergeLatency: 'Merge latency', MergeLatency: 'Merge latency', OverflowPackets: 'Overflowed packets',
  FECPackets: 'FEC packets', FECRecovered: 'FEC recovered',
};

function formatMetric(name: string, v: number): string {
  if (/BitRate$/i.test(name)) return v >= 1e6 ? `${(v / 1e6).toFixed(2)} Mbit/s` : `${Math.round(v / 1e3)} kbit/s`;
  if (/Connected$|Selected$|Active$|Status$|Scheduled$|Enabled$|Present$/.test(name)) return v >= 0.5 ? 'yes' : 'no';
  if (/UpTime$/.test(name)) return v >= 3600 ? `${(v / 3600).toFixed(1)} h` : `${Math.round(v / 60)} min`;
  if (/Latency$|Jitter$/.test(name)) return `${Math.round(v)} ms`;
  if (/Percent$/.test(name)) return `${v.toFixed(2)}%`;
  if (/RoundTripTime$/.test(name)) return `${Math.round(v)} ms`;
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

const pick = (m: Record<string, number> | undefined, re: RegExp): [string, number] | null => {
  const k = m && Object.keys(m).find((x) => re.test(x));
  return k ? [k, m![k]] : null;
};

function MetricList({ metrics }: { metrics: Record<string, number> }) {
  const keys = Object.keys(metrics).sort((a, b) => (METRIC_LABEL[a] ?? a).localeCompare(METRIC_LABEL[b] ?? b));
  if (!keys.length) return <div className="text-xs text-ink-muted">Nothing reported yet - CloudWatch is a minute or two behind.</div>;
  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-2 lg:grid-cols-3">
      {keys.map((k) => (
        <div key={k} className="flex justify-between gap-2" title={k}>
          <span className="text-ink-muted">{METRIC_LABEL[k] ?? k}</span>
          <span className="tabular" style={/Error|Loss|Dropped|NotRecovered|Disconnections/.test(k) && metrics[k] > 0 ? { color: 'var(--color-warning)' } : undefined}>
            {formatMetric(k, metrics[k])}
          </span>
        </div>
      ))}
    </div>
  );
}

/** Called after the page's early returns, so a plain function - not a hook. */
function sourceMetrics(all: Record<string, number> | undefined) {
  if (!all) return undefined;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(all)) {
    if (k.startsWith('Output') || k === 'ConnectedOutputs') continue;
    if (!k.startsWith('Source') && `Source${k}` in all) continue;
    out[k] = v;
  }
  return out;
}

export function FlowDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const opts = useOptions();
  const [data, setData] = useState<FlowDetailData | null>(null);
  const [live, setLive] = useState<Live | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [sourceEdit, setSourceEdit] = useState<FlowSource | null>(null);
  const [outEdit, setOutEdit] = useState<{ original?: string; value: FlowOutput } | null>(null);
  const [startAsk, setStartAsk] = useState(false);
  const [startHours, setStartHours] = useState<number | null>(null);
  const [timerAsk, setTimerAsk] = useState(false);
  const [timerHours, setTimerHours] = useState<number | null>(4);
  const [deleteAsk, setDeleteAsk] = useState(false);
  const [stopAsk, setStopAsk] = useState(false);
  const [removeAsk, setRemoveAsk] = useState<string | null>(null);
  const [renameAsk, setRenameAsk] = useState(false);
  const [renameTo, setRenameTo] = useState('');
  const [tplAsk, setTplAsk] = useState(false);
  const [tplName, setTplName] = useState('');
  const [showAllSource, setShowAllSource] = useState(false);
  const [openOutput, setOpenOutput] = useState<string | null>(null);

  const load = useCallback(() =>
    api.get<{ flow: FlowDetailData }>(`/flows/${id}`).then((r) => { setData(r.flow); })
      .catch((err: Error) => setError(err.message)), [id]);
  const loadLive = useCallback(() =>
    api.get<{ live: Live }>(`/flows/${id}/live`).then((r) => setLive(r.live)).catch(() => undefined), [id]);

  const state = data?.state;
  const moving = !!data && (data.busy || TRANSITIONAL.includes(data.state));
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), moving ? 3000 : 8000);
    return () => clearInterval(t);
  }, [load, moving]);
  useEffect(() => {
    if (state !== 'running') { setLive(null); return; }
    void loadLive();
    const t = setInterval(() => void loadLive(), 5000);
    return () => clearInterval(t);
  }, [loadLive, state]);

  /** One action: run it, say what happened, read the flow again. */
  const act = async (fn: () => Promise<unknown>, done: string) => {
    setWorking(true); setError(null); setNotice(null);
    try { await fn(); setNotice(done); }
    catch (err) { setError(err instanceof ApiError ? err.message : 'That did not work.'); }
    finally { setWorking(false); void load(); }
  };

  const video = useMemo(() => live?.metadata?.programs.flatMap((p) => p.streams).find((s) => s.width), [live]);
  const audio = useMemo(() => live?.metadata?.programs.flatMap((p) => p.streams).filter((s) => s.channels) ?? [], [live]);

  if (error && !data) return <Alert tone="critical">{error}</Alert>;
  if (!data || !opts) return <Spinner label="Loading flow…" />;

  const can = scopedCan(auth.can, data.access);
  const running = data.state === 'running';
  const gone = data.state === 'deleted';
  const canEdit = can('resource:edit') && !gone && !!data.arn && data.state !== 'creating';
  // CloudWatch reports most things twice at flow level (BitRate and
  // SourceBitRate) and rolls every output up beside them; the source's card
  // shows the source's own, once.
  const src = sourceMetrics(live?.metrics?.source);
  const connected = pick(src, /^(Source)?Connected$/);
  const bitrate = pick(src, /^(Source)?BitRate$/);
  const loss = pick(src, /PacketLossPercent$/);
  const rtt = pick(src, /RoundTripTime$/);
  const outputNames = data.outputs.map((o) => o.name);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/encoders" className="text-sm text-ink-muted hover:text-ink">← Encoders &amp; flows</Link>
        <h1 className="text-lg font-semibold">{data.name}</h1>
        <StateBadge state={data.state} progress={data.progress} />
        {running && (data.stopAt
          ? <span className="text-xs text-ink-secondary"><Countdown to={data.stopAt} /></span>
          : <span className="text-xs text-ink-muted">no stop timer</span>)}
        <span className="flex-1" />
        {running && can('resource:stop') && (
          <Button variant="ghost" disabled={moving} onClick={() => { setTimerHours(4); setTimerAsk(true); }}>{data.stopAt ? 'Change timer' : 'Stop timer…'}</Button>
        )}
        {data.state === 'stopped' && can('resource:start') && (
          <Button variant="primary" disabled={moving || working} onClick={() => { setStartHours(null); setStartAsk(true); }}>Start</Button>
        )}
        {running && can('resource:stop') && (
          <Button disabled={moving || working} onClick={() => setStopAsk(true)}>Stop</Button>
        )}
        {data.state === 'stopped' && canEdit && !data.encoderId && (
          <Button variant="ghost" disabled={moving || working} onClick={() => { setRenameTo(data.name); setRenameAsk(true); }}>Rename</Button>
        )}
        {data.encoderId
          ? <Link to={`/encoders/${data.encoderId}`}><Button variant="ghost">Its encoder →</Button></Link>
          : can('resource:create') && !gone && data.arn && data.state !== 'creating' && (
            <Link to={`/launch/encoder?flow=${data.id}`}>
              <Button variant="ghost" title="Add a MediaLive encoder that takes its picture from this flow - for RTMP, recording or re-encoded outputs. The flow keeps running.">+ Add encoder</Button>
            </Link>
          )}
        {can('resource:create') && !gone && (
          <>
            <Button variant="ghost" onClick={() => { setTplName(data.name); setTplAsk(true); }}>Save as template</Button>
            <Link to={`/launch/flow?from=${data.id}`}><Button variant="ghost">Duplicate flow</Button></Link>
          </>
        )}
        {can('resource:terminate') && !gone && (
          <Button variant="ghost" disabled={moving || working} onClick={() => setDeleteAsk(true)}>Delete</Button>
        )}
      </div>

      {error && <Alert tone="critical">{error}</Alert>}
      {notice && <Alert tone="good">{notice}</Alert>}
      {data.error && <Alert tone="critical" title="Something went wrong">{data.error}</Alert>}
      {gone && <Alert tone="info">This flow has been deleted. Its costs are kept below.</Alert>}
      {data.encoderId && !gone && (
        <Alert tone="info">
          An encoder takes its picture from this flow (<Link className="text-series-1 hover:underline" to={`/encoders/${data.encoderId}`}>open it</Link>).
          The flow is still run from here; it cannot be stopped while that encoder is running, or deleted while it exists.
        </Alert>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Picture" className="lg:col-span-1">
          <div className="p-3">
            <div className="aspect-video w-full overflow-hidden rounded bg-plane">
              {live?.thumbnail
                ? <img src={live.thumbnail} alt="What the source is sending" className="h-full w-full object-contain" />
                : <div className="flex h-full items-center justify-center px-4 text-center text-xs text-ink-muted">
                    {running ? 'No picture yet - nothing is arriving, or the first frame is on its way.' : 'Pictures appear while the flow is running.'}
                  </div>}
            </div>
            {video && (
              <div className="mt-2 text-xs text-ink-secondary">
                {video.codec || video.type} · {video.width}×{video.height}{video.fps ? ` · ${video.fps} fps` : ''}
              </div>
            )}
            {audio.length > 0 && (
              <div className="text-xs text-ink-muted">
                {audio.length} audio stream{audio.length === 1 ? '' : 's'}: {audio.map((a) => `${a.codec || a.type} ${a.channels}ch${a.sampleRate ? ` ${a.sampleRate / 1000}k` : ''}`).join(', ')}
              </div>
            )}
            {live?.metadata?.messages.map((m, i) => (
              <div key={i} className="mt-1 text-xs" style={{ color: 'var(--color-warning)' }}>⚠ {m.message || m.code}</div>
            ))}
          </div>
        </Card>

        <div className="space-y-4 lg:col-span-2">
          <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2 sm:grid-cols-4">
            <StatTile label="Source" tone={running ? (connected ? (connected[1] >= 0.5 ? 'good' : 'warning') : 'default') : 'default'}
              value={running ? (connected ? (connected[1] >= 0.5 ? 'Connected' : 'Waiting') : '…') : '—'}
              sub={running && connected && connected[1] < 0.5 ? 'nothing is sending to it' : undefined} />
            <StatTile label="Bitrate" value={bitrate ? formatMetric(bitrate[0], bitrate[1]) : '—'} />
            <StatTile label={rtt ? 'Round trip' : 'Packet loss'} tone={loss && loss[1] > 0.5 ? 'warning' : 'default'}
              value={rtt ? formatMetric(rtt[0], rtt[1]) : loss ? formatMetric(loss[0], loss[1]) : '—'}
              sub={rtt && loss ? `${formatMetric(loss[0], loss[1])} loss` : undefined} />
            <StatTile label={running ? 'Cost now' : 'Cost so far'} value={running ? `${formatUsd(data.costs.perHourNow || data.costs.usdPerHour, 3)}/hr` : formatUsd(data.costs.estimatedToDate, 2)}
              sub={running ? `${formatUsd(data.costs.sessionUsd, 2)} this run` : `${data.costs.hours} h run`} />
          </div>

          <Card title="Source" action={canEdit && <Button variant="ghost" disabled={moving || working} onClick={() => setSourceEdit({ ...data.spec.source })}>Edit</Button>}>
            <div className="space-y-2 p-4 text-sm">
              <div>{describeSource(data.spec.source)}{data.spec.source.cidr ? ` · from ${data.spec.source.cidr}` : ''}{data.spec.source.streamId ? ` · stream id ${data.spec.source.streamId}` : ''}</div>
              {data.sourceAddress && data.spec.source.protocol !== 'srt-caller' && (
                <div><span className="text-xs text-ink-muted">Send to </span><Copyable text={data.sourceAddress} /></div>
              )}
              {data.spec.source.protocol === 'srt-caller' && <div className="text-xs text-ink-muted">The flow {data.sourceAddress}.</div>}
              {running && src && (
                <div className="pt-1">
                  <button className="text-xs text-series-1 hover:underline" onClick={() => setShowAllSource((v) => !v)}>
                    {showAllSource ? 'Hide' : 'Show'} all {Object.keys(src).length} measurements
                  </button>
                  {showAllSource && <div className="mt-2"><MetricList metrics={src} /></div>}
                </div>
              )}
              {live?.metrics?.error && <div className="text-xs text-ink-muted">Measurements unavailable: {live.metrics.error}</div>}
            </div>
          </Card>
        </div>
      </div>

      <Card
        title={`Outputs (${data.outputs.length})`}
        action={canEdit && (
          <Button variant="ghost" disabled={moving || working} onClick={() => setOutEdit({ value: {
            ...opts.defaultOutput, name: data.outputs.length ? nextName(data.outputs[data.outputs.length - 1].name, outputNames) : 'OUT-1',
            port: nextPort(7000, data.spec.source, data.spec.outputs),
          } })}>+ Add output</Button>
        )}
      >
        {data.outputs.length === 0 ? (
          <p className="p-4 text-sm text-ink-muted">No outputs. Add one for each receiver - it can be done while the flow runs.</p>
        ) : (
          <div className="divide-y divide-grid">
            {data.outputs.map((o) => {
              const m = live?.metrics?.outputs[o.name];
              const oc = pick(m, /Connected$/);
              const ob = pick(m, /BitRate$/);
              const off = o.enabled === false;
              return (
                <div key={o.name} className="space-y-1 p-4">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-medium">{o.name}</span>
                    <span className="text-xs text-ink-muted">{PROTOCOL_LABEL[o.protocol] ?? o.protocol}{o.minLatency != null ? ` · ${o.minLatency} ms` : ''}{o.cidr && o.cidr !== '0.0.0.0/0' ? ` · from ${o.cidr}` : ''}</span>
                    {off
                      ? <span className="text-xs text-ink-muted">switched off</span>
                      : running && (
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          <span className="h-2 w-2 rounded-full" style={{ background: oc ? (oc[1] >= 0.5 ? 'var(--color-good)' : 'var(--color-warning)') : 'var(--color-ink-muted)' }} />
                          <span className="text-ink-secondary">{oc ? (oc[1] >= 0.5 ? 'connected' : isPull(o.protocol) ? 'nobody pulling' : 'not connected') : 'sending'}{ob ? ` · ${formatMetric(ob[0], ob[1])}` : ''}</span>
                        </span>
                      )}
                    <span className="flex-1" />
                    {m && Object.keys(m).length > 0 && (
                      <button className="text-xs text-series-1 hover:underline" onClick={() => setOpenOutput(openOutput === o.name ? null : o.name)}>{openOutput === o.name ? 'less' : 'more'}</button>
                    )}
                    {can('resource:stop') && canEdit && (
                      <Button variant="ghost" disabled={moving || working}
                        onClick={() => void act(() => api.post(`/flows/${id}/outputs/${o.name}/enabled`, { enabled: off }), `${o.name} switched ${off ? 'on' : 'off'}.`)}>
                        {off ? 'Turn on' : 'Turn off'}
                      </Button>
                    )}
                    {canEdit && (
                      <>
                        <Button variant="ghost" disabled={moving || working} onClick={() => setOutEdit({ original: o.name, value: { ...o } })}>Edit</Button>
                        <Button variant="ghost" disabled={moving || working} onClick={() => {
                          const copy = duplicateOf(o, data.spec.source, data.spec.outputs);
                          // A listener's copy is ready as it is; anything that pushes needs its destination checked first.
                          if (o.protocol === 'srt-listener') void act(() => api.post(`/flows/${id}/outputs`, { outputs: [copy] }), `${copy.name} added on port ${copy.port}.`);
                          else setOutEdit({ value: copy });
                        }}>Duplicate</Button>
                        <Button variant="ghost" disabled={moving || working} onClick={() => setRemoveAsk(o.name)}>Remove</Button>
                      </>
                    )}
                  </div>
                  {o.address && <div className="text-xs">{isPull(o.protocol) ? <Copyable text={o.address} /> : <code className="break-all font-mono">{o.address}</code>}{o.streamId ? <span className="text-ink-muted"> · stream id {o.streamId}</span> : null}</div>}
                  {!o.address && isPull(o.protocol) && <div className="text-xs text-ink-muted">Its address appears once the flow has started.</div>}
                  {openOutput === o.name && m && <div className="pt-2"><MetricList metrics={m} /></div>}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {!gone && data.arn && data.state !== 'creating' && (
        <RecorderCard kind="flow" id={data.id} canUse={can('resource:start')} canEdit={can('resource:edit')} />
      )}

      <Card title="Cost">
        <div className="grid grid-cols-1 gap-3 p-4 min-[400px]:grid-cols-2 sm:grid-cols-4">
          <StatTile label="Rate while running" value={`${formatUsd(data.costs.usdPerHour, 3)}/hr`} sub="from this account's bill" />
          <StatTile label="This run" value={running ? formatUsd(data.costs.sessionUsd, 2) : '—'} />
          <StatTile label="Estimated so far" value={formatUsd(data.costs.estimatedToDate, 2)} sub={`${data.costs.hours} h running`} />
          <StatTile label="Billed by AWS" value={formatUsd(data.costs.billed.total, 2)} sub={data.costs.billed.byDay.length ? `to ${data.costs.billed.byDay[data.costs.billed.byDay.length - 1].day}` : 'arrives a day or so later'} />
        </div>
        <div className="border-t border-grid px-4 py-2 text-xs text-ink-muted">
          {opts.estimate.note} {data.az ? `Zone ${data.az}.` : ''} Made {formatRelative(data.createdAt)}.
        </div>
      </Card>

      {/* --- dialogs --- */}
      <Modal open={!!sourceEdit} onClose={() => setSourceEdit(null)} title="Edit source" width="max-w-3xl"
        footer={<>
          <Button variant="ghost" onClick={() => setSourceEdit(null)}>Cancel</Button>
          <Button variant="primary" loading={working} onClick={() => { const v = sourceEdit!; setSourceEdit(null); void act(() => api.patch(`/flows/${id}/source`, v), 'Source updated.'); }}>Save</Button>
        </>}>
        {sourceEdit && (
          <div className="space-y-3">
            {running && <Alert tone="warning">The flow is running: changing the source drops the incoming feed for a moment, and a new port or type means the sender has to change too.</Alert>}
            <SourceFields opts={opts} value={sourceEdit} onChange={setSourceEdit} />
          </div>
        )}
      </Modal>

      <Modal open={!!outEdit} onClose={() => setOutEdit(null)} title={outEdit?.original ? `Edit ${outEdit.original}` : 'Add output'} width="max-w-3xl"
        footer={<>
          <Button variant="ghost" onClick={() => setOutEdit(null)}>Cancel</Button>
          <Button variant="primary" loading={working} disabled={!outEdit?.value.name || outEdit?.value.host === opts.placeholderHost} onClick={() => {
            const e = outEdit!; setOutEdit(null);
            void act(
              () => (e.original ? api.patch(`/flows/${id}/outputs/${e.original}`, e.value) : api.post(`/flows/${id}/outputs`, { outputs: [e.value] })),
              e.original ? `${e.value.name} updated.` : `${e.value.name} added.`,
            );
          }}>{outEdit?.original ? 'Save' : 'Add'}</Button>
        </>}>
        {outEdit && (
          <div className="space-y-3">
            {outEdit.original && running && <Alert tone="warning">Whoever is taking this output is dropped for a moment while it changes.</Alert>}
            {outEdit.original && outEdit.value.name !== outEdit.original && <Alert tone="info">A new name makes a new output with these settings and removes {outEdit.original}.</Alert>}
            <OutputFields opts={opts} value={outEdit.value} onChange={(v) => setOutEdit({ ...outEdit, value: v })} />
          </div>
        )}
      </Modal>

      <Modal open={startAsk} onClose={() => setStartAsk(false)} title={`Start ${data.name}`}
        footer={<>
          <Button variant="ghost" onClick={() => setStartAsk(false)}>Cancel</Button>
          <Button variant="primary" onClick={() => { setStartAsk(false); void act(() => api.post(`/flows/${id}/start`, { stopAfterHours: startHours }), 'Starting.'); }}>Start</Button>
        </>}>
        <div className="space-y-3">
          <Field label="Stop timer"><TimerSelect value={startHours} onChange={setStartHours} /></Field>
          <p className="text-sm text-ink-muted">{formatUsd(data.costs.usdPerHour, 3)}/hr while it runs{startHours ? ` - about ${formatUsd(data.costs.usdPerHour * startHours, 2)}` : ''}.</p>
        </div>
      </Modal>

      <Modal open={timerAsk} onClose={() => setTimerAsk(false)} title="Stop timer"
        footer={<>
          <Button variant="ghost" onClick={() => setTimerAsk(false)}>Cancel</Button>
          <Button variant="primary" onClick={() => { setTimerAsk(false); void act(() => api.post(`/flows/${id}/timer`, { hours: timerHours }), timerHours ? `It will stop in ${timerHours} hour${timerHours === 1 ? '' : 's'}.` : 'Timer cleared - it runs until stopped.'); }}>Set</Button>
        </>}>
        <Field label="From now" hint={data.stopAt ? 'Replaces the timer it has.' : undefined}><TimerSelect value={timerHours} onChange={setTimerHours} /></Field>
      </Modal>

      <Modal open={tplAsk} onClose={() => setTplAsk(false)} title="Save as template"
        footer={<>
          <Button variant="ghost" onClick={() => setTplAsk(false)}>Cancel</Button>
          <Button variant="primary" disabled={tplName.trim().length < 2} onClick={() => { setTplAsk(false); void act(() => api.post('/flows/templates', { name: tplName, flowId: id }), `Saved as the template "${tplName}".`); }}>Save</Button>
        </>}>
        <div className="space-y-3">
          <Field label="Template name"><Input autoFocus value={tplName} maxLength={80} onChange={(e) => setTplName(e.target.value)} /></Field>
          <p className="text-sm text-ink-muted">Keeps the source and every output as they are now. Addresses it pushes to are blanked, so a new flow from it cannot send to this one's receivers by mistake.</p>
        </div>
      </Modal>

      <Confirm
        open={stopAsk}
        title={`Stop ${data.name}?`}
        message="Every receiver loses the feed until it is started again, and the source has nowhere to send. Starting takes under a minute."
        confirmLabel="Stop the flow"
        danger
        busy={working}
        onClose={() => setStopAsk(false)}
        onConfirm={() => { setStopAsk(false); void act(() => api.post(`/flows/${id}/stop`), 'Stopping.'); }}
      />
      <Confirm
        open={!!removeAsk}
        title={`Remove ${removeAsk ?? ''}?`}
        message="Whoever is taking this output loses the feed, and its address stops working."
        confirmLabel="Remove"
        danger
        busy={working}
        onClose={() => setRemoveAsk(null)}
        onConfirm={() => { const n = removeAsk!; setRemoveAsk(null); void act(() => api.delete(`/flows/${id}/outputs/${n}`), `${n} removed.`); }}
      />

      <Modal open={renameAsk} onClose={() => setRenameAsk(false)} title={`Rename ${data.name}`}
        footer={<>
          <Button variant="ghost" onClick={() => setRenameAsk(false)}>Cancel</Button>
          <Button variant="danger" disabled={!renameTo.trim() || renameTo.trim().toUpperCase() === data.name} onClick={() => {
            setRenameAsk(false);
            void act(() => api.post(`/flows/${id}/rename`, { name: renameTo.trim() }), 'Renaming - rebuilding the flow under its new name.');
          }}>Rename and rebuild</Button>
        </>}>
        <div className="space-y-3 text-sm">
          <Field label="New name"><Input autoFocus value={renameTo} maxLength={40} onChange={(e) => setRenameTo(e.target.value.toUpperCase())} /></Field>
          <Alert tone="warning" title="Renaming rebuilds the flow">
            MediaConnect cannot rename a flow, so it is deleted and made again from its settings under the new name,
            a minute or so. <b>Its addresses change</b>: the ingest address the source sends to and every output
            address receivers pull from, so they all need repointing. A recorder attached to it is removed.
          </Alert>
        </div>
      </Modal>

      <Modal open={deleteAsk} onClose={() => setDeleteAsk(false)} title={`Delete ${data.name}`}
        footer={<>
          <Button variant="ghost" onClick={() => setDeleteAsk(false)}>Cancel</Button>
          <Button variant="danger" onClick={() => {
            setDeleteAsk(false);
            void act(() => api.delete(`/flows/${id}`, { confirmName: data.name }), 'Deleting.').then(() => { if (!running) navigate('/encoders'); });
          }}>Delete the flow</Button>
        </>}>
        <div className="space-y-3 text-sm">
          <Alert tone="critical" title="This cannot be undone">
            {running ? 'It is running now. ' : ''}
            The flow is deleted with its ingest address and every output address: the source has nowhere to send and
            every receiver loses the feed.
          </Alert>
          <p className="text-ink-secondary">
            Kept: its costs, on the Costs page. To keep it for later, <b>Stop</b> it instead: a stopped flow costs
            nothing and keeps its addresses.
          </p>
        </div>
      </Modal>
    </div>
  );
}
